import { Fragment } from 'react';
import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';
import NsContent from 'components/common/ns-content/NsContent';

import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { NsDescriptionList } from 'components/common/ns-description-list/NsDescriptionList';
import { NsCard } from 'components/common/ns-card/NsCard';
import { NsList } from 'components/common/ns-list/NsList';
import { NsCaveatLink } from 'components/common/ns-caveat-link/NsCaveatLink';

export const testId = 'tariffdetailsCardTestId';

type TariffDetailsCardProps = {
  tariffDetails: {
    common: {
      tariffName: string;
      energyType: string;
      tariffType: string;
      paymentType: string;
      endDate: string;
      additionalProducts: string;
      termsAndConditions: string;
      exitFee: string;
    };
    electricity: {
      standingCharge: string;
      unitRate: string;
      unitRateDay?: string;
      unitRateDayName?: string;
      unitRateNight?: string;
      unitRateNightName?: string;
      exitFee: string;
    };
    gas: {
      standingCharge: string;
      unitRate: string;
      exitFee: string;
    };
    annualProjection: {
      electricity: {
        consumption: string;
        price: string;
      };
      gas: {
        consumption: string;
        price: string;
      };
      total: {
        price: string;
      };
    };
  };
};

export const TariffDetailsCard = ({
  tariffDetails
}: TariffDetailsCardProps) => {
  const { common, gas, electricity, annualProjection } = tariffDetails;

  const exitFeeText = common.exitFee
    ? `Exit fee ${common.exitFee}`
    : 'No Exit Fee';
  const standingChargeCaveat = 'standingChargeCaveat';

  return (
    <NsContent data-testid={testId}>
      <h2>{common.tariffName}</h2>
      <NsColumn hockeypitch="2:1">
        <NsContent>
          <NsList>
            {[
              <Fragment key="0">
                {common.tariffType}
                {` `}
                <NsCaveatLink>
                  <p>
                    <strong>Variable</strong> - With a variable price tariff
                    your energy prices may increase or decrease at any time, but
                    we&apos;ll give you advance notice before we increase your
                    prices. See the tariff terms and conditions for more
                    information.
                    <br />
                    <strong>Fixed</strong> - With a fixed price tariff your
                    energy prices are fixed until your tariff ends unless it is
                    a tariff where the price may decrease. Or the government or
                    regulator does something that changes the price. See the
                    tariff terms and conditions for more information.
                    <br />
                    <strong>Tracker</strong> - The price of this tariff may go
                    up or down. The price of the tariff for your payment method
                    and region will track the Ofgem maximum charge. See the
                    tariff terms and conditions for more information.
                    <br />
                    View our{' '}
                    <NsAnchor
                      target={undefined}
                      href="https://www.britishgas.co.uk/energy/guides/fixed-vs-variable-energy.html"
                    >
                      tariff guide
                    </NsAnchor>{' '}
                    to see which tariff type tariff is right for you.
                  </p>
                </NsCaveatLink>
              </Fragment>,
              <Fragment key="1">Tariff ends on {common.endDate}</Fragment>,
              <Fragment key="2">{common.paymentType}</Fragment>,
              <Fragment key="3">
                {exitFeeText}{' '}
                <NsCaveatLink>
                  <p>
                    You won&apos;t pay an exit fee if you switch to another
                    British Gas fixed tariff.
                  </p>
                </NsCaveatLink>
              </Fragment>
            ]}
          </NsList>
        </NsContent>
        <NsCard>
          <p>
            Total estimated costs{` `}
            <NsCaveatLink>
              <p>
                The estimated annual cost is a calculation of what you&apos;ll
                pay for 12 months using the tariff unit rate for the area where
                you live, the daily standing charge, the national average annual
                consumption and also includes VAT.
              </p>
            </NsCaveatLink>
          </p>
          <p>
            <span className="h3">{annualProjection.total.price}</span> / year
          </p>
        </NsCard>
      </NsColumn>
      <NsColumn hockeypitch="2">
        {electricity && (
          <NsCard heading="Electricity" decoration="bulb">
            <p>All costs are inclusive of VAT.</p>
            <NsDescriptionList className="dl-row">
              {[
                ...(electricity.unitRate
                  ? [{ term: 'Unit rate', description: electricity.unitRate }]
                  : []),
                ...(electricity.unitRateDay
                  ? [
                      {
                        term: electricity.unitRateDayName,
                        description: electricity.unitRateDay
                      }
                    ]
                  : []),
                ...(electricity.unitRateNight
                  ? [
                      {
                        term: electricity.unitRateNightName,
                        description: electricity.unitRateNight
                      }
                    ]
                  : []),
                {
                  term: (
                    <>
                      Standing charge{' '}
                      <NsCaveatLink caveatId={standingChargeCaveat}>
                        The standing charge is applied on a pence per day basis.
                        It helps cover an energy supplier&apos;s fixed costs.
                      </NsCaveatLink>
                    </>
                  ),
                  description: electricity.standingCharge
                },
                {
                  term: 'Estimated annual cost',
                  description: annualProjection.electricity.price
                },
                {
                  term: 'Estimated annual usage',
                  description: annualProjection.electricity.consumption
                },
                {
                  term: 'Additional products or services included',
                  description: common.additionalProducts
                }
              ]}
            </NsDescriptionList>
          </NsCard>
        )}
        {gas && (
          <NsCard heading="Gas" decoration="gas">
            <p>All costs are inclusive of VAT.</p>
            <NsDescriptionList className="dl-row">
              {[
                ...(gas.unitRate
                  ? [{ term: 'Unit rate', description: gas.unitRate }]
                  : []),
                {
                  term: (
                    <>
                      Standing charge{' '}
                      <NsCaveatLink caveatId={standingChargeCaveat}>
                        The standing charge is applied on a pence per day basis.
                        It helps cover an energy supplier&apos;s fixed costs.
                      </NsCaveatLink>
                    </>
                  ),
                  description: gas.standingCharge
                },
                {
                  term: 'Estimated annual cost',
                  description: annualProjection.gas.price
                },
                {
                  term: 'Estimated annual usage',
                  description: annualProjection.gas.consumption
                },
                {
                  term: 'Additional products or services included',
                  description: common.additionalProducts
                }
              ]}
            </NsDescriptionList>
          </NsCard>
        )}
      </NsColumn>
    </NsContent>
  );
};
